import React, { useEffect, useState } from 'react'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X, Check } from 'lucide-react'
import axios from '../../../config/axios'
import RenderField from '../RenderField'
import styled from 'styled-components'

interface IntegrationConfig {
  moduleId: string
  moduleName: string
  endpoint: string
  createEndpoint?: string
  fieldKey: string
  fieldName: string
  integrationType: 'read' | 'write'
  isRequired?: boolean
}

interface RequiredField {
  pipelineFieldId: number
  pipelineField: {
    id: number
    name: string
    typeFieldId: number
    typeField?: {
      id: number
      name: string
    }
    integrationConfig?: IntegrationConfig
  }
  isRequired: boolean
  isFilled: boolean
  currentValue: any
}

interface RequiredFieldsData {
  ticketId: number
  ticketTitle: string
  sourceStepId: number
  sourceStepName: string
  targetStepId: number
  targetStepName: string
  requiredFields: RequiredField[]
  allFilled: boolean
}

interface FieldInTicket {
  ticketId?: number
  pipelineFieldId: number
  stringValue?: string
  bigStringValue?: string
  integerValue?: number
  decimalValue?: number
  dateTimeValue?: string
  booleanValue?: boolean
  pipelineField: {
    id: number
    name: string
    typeFieldId: number
    typeField?: {
      id: number
      name: string
    }
    integrationConfig?: IntegrationConfig
  }
  isRequired: boolean
}

interface TicketData {
  id: number
  title: string
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  fieldInTickets: FieldInTicket[]
}

interface PropsInterface {
  ticketId: number
  targetStepId: number
  onClose: () => void
  onSuccess: () => void
}

const CardLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #0090D3;
  text-transform: uppercase;
`

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1F2937;
  margin: 0;
`

const ModalDescription = styled.p`
  font-size: 14px;
  color: #6B7280;
  margin: 0 0 1.5rem 0;
`

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FillRequiredFieldsModal: React.FC<PropsInterface> = ({ 
  ticketId, 
  targetStepId, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [data, setData] = useState<RequiredFieldsData | null>(null)
  const [ticketData, setTicketData] = useState<TicketData | null>(null)

  useEffect(() => {
    const fetchRequiredFields = async () => {
      try {
        setLoading(true)
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/required-fields/${targetStepId}`
        )
        if (response.data?.data) {
          setData(response.data.data)
          
          const fieldInTickets: FieldInTicket[] = response.data.data.requiredFields.map((rf: RequiredField) => ({
            pipelineFieldId: rf.pipelineFieldId,
            pipelineField: rf.pipelineField,
            isRequired: true,
            stringValue: rf.currentValue?.stringValue || undefined,
            bigStringValue: rf.currentValue?.bigStringValue || undefined,
            integerValue: rf.currentValue?.integerValue || undefined,
            decimalValue: rf.currentValue?.decimalValue || undefined,
            dateTimeValue: rf.currentValue?.dateTimeValue || undefined,
            booleanValue: rf.currentValue?.booleanValue || undefined
          }))

          setTicketData({
            id: ticketId,
            title: response.data.data.ticketTitle,
            deadline: new Date(),
            urgency: false,
            description: '',
            stepId: targetStepId,
            fieldInTickets
          })
        }
      } catch (error) {
        console.error('Erro ao buscar campos obrigatórios:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchRequiredFields()
  }, [ticketId, targetStepId])

  const handleSave = async () => {
    if (!ticketData || !data) return

    const errors: string[] = []
    const isEmail = (v: string) => /.+@.+\..+/.test(v)
    const onlyDigits = (v: string) => v.replace(/\D/g, '')
    const isCpfValid = (v: string) => onlyDigits(v).length === 11
    const isCnpjValid = (v: string) => onlyDigits(v).length === 14
    const isCelularValid = (v: string) => {
      const d = onlyDigits(v)
      return d.length >= 10 && d.length <= 11
    }
    const isTelefoneValid = (v: string) => onlyDigits(v).length === 10

    ticketData.fieldInTickets.forEach((f) => {
      const typeName = f.pipelineField.typeField?.name || ''
      const hasIntegration = !!f.pipelineField.integrationConfig

      const isStringType = ['E-mail', 'CPF', 'CNPJ', 'Celular', 'Telefone', 'Texto simples'].includes(typeName)
      const isDateType = typeName === 'Data'
      const isCheckboxType = typeName === 'Checkbox'
      const isValorType = typeName === 'Valor (R$)'

      if (hasIntegration && !f.integerValue) {
        errors.push(`Selecione um valor para ${f.pipelineField.name}`)
      } else if (!hasIntegration) {
        if (isStringType && (!f.stringValue || f.stringValue.trim() === '')) errors.push(`Preencha o campo ${f.pipelineField.name}`)
        if (isDateType && !f.dateTimeValue) errors.push(`Preencha o campo ${f.pipelineField.name}`)
        if (isValorType && (f.decimalValue === undefined || f.decimalValue === null)) errors.push(`Preencha o campo ${f.pipelineField.name}`)
      }

      if (typeName === 'E-mail' && f.stringValue && !isEmail(f.stringValue)) errors.push(`E-mail inválido em ${f.pipelineField.name}`)
      if (typeName === 'CPF' && f.stringValue && !isCpfValid(f.stringValue)) errors.push(`CPF inválido em ${f.pipelineField.name}`)
      if (typeName === 'CNPJ' && f.stringValue && !isCnpjValid(f.stringValue)) errors.push(`CNPJ inválido em ${f.pipelineField.name}`)
      if (typeName === 'Celular' && f.stringValue && !isCelularValid(f.stringValue)) errors.push(`Celular inválido em ${f.pipelineField.name}`)
      if (typeName === 'Telefone' && f.stringValue && !isTelefoneValid(f.stringValue)) errors.push(`Telefone inválido em ${f.pipelineField.name}`)
    })

    if (errors.length > 0) {
      alert(errors[0])
      return
    }

    try {
      setSaving(true)
      await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        stepId: targetStepId,
        fieldInTickets: ticketData.fieldInTickets
      })
      onSuccess()
    } catch (error) {
      console.error('Erro ao salvar campos:', error)
      alert('Erro ao salvar campos. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <ModalTest>
        <S.ContainerOfModal width='500px'>
          <S.Header>
            <S.HeaderTitle>Carregando...</S.HeaderTitle>
          </S.Header>
        </S.ContainerOfModal>
      </ModalTest>
    )
  }

  if (!data || !ticketData) {
    return null
  }

  return (
    <ModalTest>
      <S.ContainerOfModal width='500px'>
        <S.Header>
          <div>
            <CardLabel>CARD: {data.ticketTitle.toUpperCase()}</CardLabel>
            <ModalTitle>Preenchimento de campo</ModalTitle>
          </div>
          <S.CloseDiv onClick={onClose}>
            <X size={20} />
          </S.CloseDiv>
        </S.Header>

        <ModalDescription>
          Preencha os campos obrigatórios da fase <strong>{data.sourceStepName}</strong> para mover o card para <strong>{data.targetStepName}</strong>.
        </ModalDescription>

        <FieldsContainer>
          {ticketData.fieldInTickets.map((field) => (
            <RenderField
              key={field.pipelineFieldId}
              field={field}
              setValue={setTicketData as any}
              isFilled={true}
            />
          ))}
        </FieldsContainer>

        <S.ButtonContainer>
          <S.Button className='cancel' onClick={onClose}>
            Cancelar
          </S.Button>
          <S.Button onClick={handleSave} disabled={saving}>
            <Check size={16} />
            {saving ? 'Salvando...' : 'Concluir'}
          </S.Button>
        </S.ButtonContainer>
      </S.ContainerOfModal>
    </ModalTest>
  )
}

export default FillRequiredFieldsModal
